import React, { useMemo } from 'react'
import { ColorSchemeName } from 'react-native'
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context'
import { ThemeProvider, DefaultTheme } from 'styled-components/native'
import Config from 'APP/Config'
import useScreenView from 'APP/Hooks/useScreenView'
import { getColourScheme, ColourScheme } from 'APP/Helpers/AppearanceUtils'
import { logger } from 'APP/Lib/Datadog'
import * as defaultThemes from './defaultThemes'

// TODO: Move this somewhere better
type AtLeastOne<T> = { [K in keyof T]-?: Pick<T, K> & Partial<Omit<T, K>> }[keyof T]

const themes = defaultThemes as Record<string, DefaultTheme>

export function getThemeName(colorScheme: ColorSchemeName, brandId: string, brandVersion?: string) {
  const themeSuffix = colorScheme === ColourScheme.DARK ? 'Dark' : 'Light'
  const baseTheme = `${brandId}${themeSuffix}`

  if (brandVersion) {
    const versionTheme = `${baseTheme}_${brandVersion}`

    if (themes[versionTheme]) return versionTheme

    if (__DEV__)
      logger.warn(
        `Versioned theme: ${versionTheme} expected but not found. Defaulting to ${baseTheme}`
      )
  }

  return baseTheme
}

export function getEnhancedTheme(
  appTheme: DefaultTheme,
  isBigScreen: boolean,
  isSmallScreen: boolean,
  insets: EdgeInsets
): DefaultTheme {
  return {
    ...appTheme,
    isBigScreen,
    isSmallScreen,
    insets,
  }
}

export function getAppTheme({
  storybookTheme,
  defaultTheme,
}: AtLeastOne<{
  storybookTheme?: DefaultTheme
  defaultTheme?: DefaultTheme
}>): DefaultTheme {
  const theme = storybookTheme ? { ...storybookTheme } : { ...defaultTheme }

  return theme as DefaultTheme
}

export const AppThemeProvider = ({
  theme: storybookTheme,
  children,
}: {
  theme?: DefaultTheme
  children: React.ReactNode
}) => {
  const colorScheme = getColourScheme()

  const insets = useSafeAreaInsets()
  const { isBigScreen, isSmallScreen } = useScreenView()

  const themeName = useMemo(
    () => getThemeName(colorScheme, Config.BRAND_ID, Config.BRAND_VERSION),
    [colorScheme]
  )

  const appTheme = useMemo(
    () => getAppTheme({ storybookTheme, defaultTheme: themes[themeName] }),
    [storybookTheme, themeName]
  )

  const enhancedTheme = useMemo(
    () => getEnhancedTheme(appTheme, isBigScreen, isSmallScreen, insets),
    [appTheme, isBigScreen, isSmallScreen, insets]
  )

  return <ThemeProvider theme={enhancedTheme}>{children}</ThemeProvider>
}

export default AppThemeProvider
